"use client";

import { memo, useCallback, useContext, useEffect, useRef, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import type { Flash } from "@/lib/odds-screen/live/derive";
import { useLadderPrefetch, useRow } from "@/hooks/use-odds-live";
import { ladderEventOf } from "@/lib/odds-screen/live/inplay";
import type { BookId, EntityKey, EventHeader, EventId, FamilyId, FamilySummary, LadderTopic, LeagueId, MarketSpec, OddsDisplay, RowId } from "@/lib/odds-screen/live/types";
import { LatestPlay, LiveState, SideScore, sideTone } from "./live-state";
import { flashCells, lineSides, rowCells, type CellSide, type MarketCells } from "./row-model";
import { betPick, type BetActions, type BetPick } from "./bet";
import { PriceCell } from "./price-cell";
import { TeamLogo } from "@/components/ui/avatar";
import { drawName, matchupLabel, matchupSides, rowLines, scoredMatchupLabel, teamLogoUrl, type RowLine } from "./labels";
import { SubjectCell } from "./subject-cell";
import { MinStakeContext } from "./use-min-stake";
import { rowSpec, subjectOf, type Subject } from "./subject";
import { StarButton } from "./star-button";

export type HiddenRow = { readonly rowId: RowId; readonly event: EventHeader | undefined; readonly entityName: string | null };

export type CellTarget = { readonly slot: number; readonly outcome: number; readonly main: number | null; readonly historyBook?: BookId };

export type CellBet = {
  readonly subject: string;
  readonly pick: (books: readonly BookId[]) => BetPick;
  readonly onBet: (target: CellTarget, book: BookId) => void;
  readonly onPrefetch: () => void;
};

export function useCellBet(bet: BetActions, topic: LadderTopic, markets: readonly (MarketCells | null)[], subject: string, onMissing: (target: CellTarget) => void): CellBet {
  const prefetch = useLadderPrefetch();
  return {
    subject,
    pick: (books) => betPick(books, bet.hasAccount),
    onBet: (target, book) => {
      const market = markets[target.slot];
      const best = market?.sides.find((side) => side.outcome === target.outcome)?.best;
      if (!market || !best) return;
      if (topic.event === "") {
        onMissing(target);
        return;
      }
      bet.open({ topic, spec: market.spec, slot: target.slot, line: target.main, outcome: target.outcome, book, decimal: best.decimal }, () => onMissing(target));
    },
    onPrefetch: () => { if (topic.event !== "") prefetch(topic); },
  };
}

type Selected = { readonly slot: number; readonly outcome: number } | null;

function historyAction(side: CellSide, target: CellTarget, bet: CellBet, onOpen: (target: CellTarget) => void, live: boolean) {
  if (live) return {};
  const open = (event: { preventDefault: () => void }) => {
    if (side.best === null) return;
    event.preventDefault();
    onOpen({ ...target, historyBook: bet.pick(side.best.books).book });
  };
  return {
    onContextMenu: open,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) open(event);
    },
  };
}

function marketColumn(
  market: MarketCells | null,
  lines: readonly RowLine[],
  slot: number,
  flash: readonly Flash[],
  selected: Selected,
  onOpen: (target: CellTarget) => void,
  bet: CellBet,
  live: boolean,
) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {(market === null ? lines.map(() => null) : lineSides(market, lines)).map((index, row) => index === null || market === null ? (
        <div key={lines[row]} className="h-[30px]" aria-hidden="true" />
      ) : (
        <div key={lines[row]} className="contents" {...historyAction(market.sides[index], { slot, outcome: market.sides[index].outcome, main: market.main }, bet, onOpen, live)}>
          <PriceCell
            side={market.sides[index]}
            flash={flash[index] ?? null}
            selected={selected?.slot === slot}
            onOpen={() => onOpen({ slot, outcome: market.sides[index].outcome, main: market.main })}
            bet={bet}
            target={{ slot, outcome: market.sides[index].outcome, main: market.main }}
          />
        </div>
      ))}
    </div>
  );
}

export type PropsOffer = { readonly count?: number };

export function propsOffer(event: EventHeader | undefined): PropsOffer | null {
  if (event?.live !== undefined) return event.liveProps === true ? {} : null;
  return event !== undefined && event.props > 0 ? { count: event.props } : null;
}

export function PropsLink({ count, gameLabel, compact = false, isMobile = false, onOpen }: { count?: number; gameLabel: string; compact?: boolean; isMobile?: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={count === undefined ? `Player props for ${gameLabel}` : `${count} player props for ${gameLabel}`}
      className={cn(
        "tap flex flex-shrink-0 items-center gap-1 rounded-md font-medium text-zinc-500 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        compact ? "h-6 self-start text-[11px]" : isMobile ? "h-9 pl-3 pr-1 text-[12px]" : "h-[26px] px-2 text-[11px]",
      )}
    >
      <span className="data-num">{count === undefined ? "Props" : `${count} props`}</span>
      <svg width={isMobile ? 14 : 12} height={isMobile ? 14 : 12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
    </button>
  );
}

export function GameRowView({
  league, event, entityName = null, lines, drawLabel, markets, order, flash, gridCols, selected, rowSelected, onOpen, bet, onMatchup, onHide, propsLink, onOpenGame, compact, wide = false, playColumn = false, onHoverStart, onHoverEnd,
}: {
  league: LeagueId;
  event: EventHeader | undefined;
  entityName?: string | null;
  lines: readonly RowLine[];
  drawLabel?: string;
  markets: readonly (MarketCells | null)[];
  order: readonly number[];
  flash: readonly (readonly Flash[])[];
  gridCols: string;
  selected: Selected;
  rowSelected: boolean;
  onOpen: (target: CellTarget) => void;
  bet: CellBet;
  onMatchup: () => void;
  onHide: () => void;
  propsLink?: PropsOffer | null;
  onOpenGame?: () => void;
  compact?: boolean;
  wide?: boolean;
  playColumn?: boolean;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  const spokenMatchup = matchupLabel(event, league, "spoken");
  const gameLabel = spokenMatchup || "this game";
  const threeWay = lines.includes("draw");
  const propsMenu = onOpenGame !== undefined && propsLink ? (
    <PropsLink count={propsLink.count} gameLabel={gameLabel} compact={compact} onOpen={onOpenGame} />
  ) : null;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      onHide();
    }
  };
  return (
    <div
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onKeyDown={onKeyDown}
      className={cn("relative grid items-center justify-start gap-x-2 border-b border-white/[0.075] py-2 pl-7 pr-4 hover:bg-white/[0.015]", rowSelected && "bg-primary/[0.03]")}
      style={{ gridTemplateColumns: gridCols }}
    >
      <StarButton league={league} event={event} className="absolute left-0.5 top-1/2 h-7 w-6 -translate-y-1/2" />
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex min-w-0 flex-grow flex-col">
          <button
            type="button"
            onClick={onMatchup}
            aria-label={spokenMatchup ? `Open ${scoredMatchupLabel(event, league)}` : "Open game"}
            className="tap flex min-w-0 items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <div className="flex min-w-0 flex-grow flex-col gap-1">
              {entityName !== null ? (
                <>
                  <span className="flex min-w-0 items-center gap-2">
                    <TeamLogo name={entityName} imageUrl={teamLogoUrl(event, entityName)} size={20} />
                    <span className="truncate text-[13px] font-medium text-foreground">{entityName}</span>
                  </span>
                  <span className="truncate pl-7 text-[11px] text-zinc-500">{matchupLabel(event, league)}</span>
                </>
              ) : matchupSides(event, lines, drawLabel).map(({ line, name, logo, live }) => (
                <span key={line} className={cn("flex min-w-0 items-center gap-2", threeWay && "h-[30px]")}>
                  {event && line !== "draw" && <TeamLogo name={name} imageUrl={logo} size={20} />}
                  <span className={cn("truncate text-[13px] font-medium", line === "draw" ? "pl-7 text-zinc-400" : sideTone(live))}>{name}</span>
                  <SideScore live={live} className="min-w-12" inlinePeriods={wide} />
                </span>
              ))}
            </div>
          </button>
          {compact ? propsMenu : null}
        </div>
        {event?.live ? (
          <div className={cn("mx-2 flex-shrink-0", compact ? "min-w-23" : "min-w-30", playColumn && ["flex flex-col items-start gap-1", wide && "basis-1/3"])}>
            <LiveState live={event.live} compact={compact} />
            <LatestPlay play={event.live.plays[0] ?? null} className="w-0 min-w-full" />
            {compact ? null : propsMenu}
          </div>
        ) : compact ? null : propsMenu}
      </div>
      {order.map((slot) => (
        <div key={slot} className="min-w-0">
          {marketColumn(markets[slot] ?? null, lines, slot, flash[slot] ?? [], selected, onOpen, bet, Boolean(event?.live))}
        </div>
      ))}
    </div>
  );
}

export function SubjectRowView({
  subject, live, count, market, flash, gridCols, selected, rowSelected, compact = false, onOpen, bet, onHide, onHoverStart, onHoverEnd,
}: {
  subject: Subject;
  live: boolean;
  count: number;
  market: MarketCells | null;
  flash: readonly Flash[];
  gridCols: string;
  selected: Selected;
  rowSelected: boolean;
  compact?: boolean;
  onOpen: (target: CellTarget) => void;
  bet: CellBet;
  onHide: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      onHide();
    }
  };
  return (
    <div
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onKeyDown={onKeyDown}
      className={cn("grid items-center justify-start gap-x-2 border-b border-white/[0.06] py-1.5 hover:bg-white/[0.015]", compact ? "px-3" : "px-4", rowSelected && "bg-primary/[0.03]")}
      style={{ gridTemplateColumns: gridCols }}
    >
      <SubjectCell subject={subject} className={compact ? "pl-1" : "pl-[22px]"} />
      {market === null ? (
        Array.from({ length: count }, (_, index) => <div key={index} className="min-w-0" />)
      ) : (
        market.sides.map((side, index) => (
          <div key={side.outcome} className="min-w-0" {...historyAction(side, { slot: 0, outcome: side.outcome, main: market.main }, bet, onOpen, live)}>
            <PriceCell
              side={side}
              flash={flash[index] ?? null}
              selected={selected?.outcome === side.outcome}
              compact={compact}
              onOpen={() => onOpen({ slot: 0, outcome: side.outcome, main: market.main })}
              bet={bet}
              target={{ slot: 0, outcome: side.outcome, main: market.main }}
            />
          </div>
        ))
      )}
    </div>
  );
}

type FlashContext = { readonly family: FamilyId; readonly selected: ReadonlySet<BookId>; readonly format: OddsDisplay; readonly minStake: number };

function useRowCellsWithFlash(
  markets: readonly (MarketCells | null)[],
  context: FlashContext,
): readonly (readonly Flash[])[] {
  const prev = useRef<{ context: FlashContext; markets: readonly (MarketCells | null)[] }>({ context, markets: [] });
  const sameContext = prev.current.context.family === context.family && prev.current.context.selected === context.selected && prev.current.context.format === context.format && prev.current.context.minStake === context.minStake;
  const flash = flashCells(sameContext ? prev.current.markets : [], markets);
  useEffect(() => {
    prev.current = { context, markets };
  });
  return flash;
}

function useHoverPrefetch(league: LeagueId, event: EventId, family: FamilyId, entity: EntityKey) {
  const prefetch = useLadderPrefetch();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const start = useCallback(() => {
    if (event === "") return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => prefetch({ league, event, family, entity }), 90);
  }, [prefetch, league, event, family, entity]);
  const end = useCallback(() => clearTimeout(timer.current), []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { start, end };
}

export const LiveGameRow = memo(function LiveGameRow({
  league, family, rowId, event, specs, order, selected, format, gridCols, drillSlot, drillOutcome, rowSelected, compact, wide, playColumn, bet, onOpen, onHide, onOpenGame,
}: {
  league: LeagueId;
  family: FamilyId;
  rowId: RowId;
  event: EventHeader | undefined;
  specs: readonly MarketSpec[];
  order: readonly number[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  gridCols: string;
  drillSlot: number | null;
  drillOutcome: number | null;
  rowSelected: boolean;
  compact: boolean;
  wide?: boolean;
  playColumn?: boolean;
  bet: BetActions;
  onOpen: (rowId: RowId, target: CellTarget) => void;
  onHide: (row: HiddenRow) => void;
  onOpenGame: (rowId: RowId) => void;
}) {
  const row = useRow(league, family, rowId, event, specs);
  const minStake = useContext(MinStakeContext);
  const markets = row ? rowCells(row, specs, event, selected, format, minStake) : [];
  const flash = useRowCellsWithFlash(markets, { family, selected, format, minStake });
  const entity = row?.entity?.key ?? "";
  const entityName = row?.entity?.name ?? null;
  const ladderEvent = ladderEventOf(event);
  const hover = useHoverPrefetch(league, ladderEvent, family, entity);
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEvent, family, entity }, markets, entityName ?? matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, target));
  const firstSlot = order[0] ?? 0;
  return (
    <GameRowView
      league={league}
      event={event}
      entityName={entityName}
      lines={rowLines(specs, entityName !== null)}
      drawLabel={drawName(specs)}
      markets={markets}
      order={order}
      flash={flash}
      gridCols={gridCols}
      selected={rowSelected && drillSlot !== null ? { slot: drillSlot, outcome: drillOutcome ?? 0 } : null}
      rowSelected={rowSelected}
      onOpen={(target) => onOpen(rowId, target)}
      bet={cellBet}
      onMatchup={() => onOpen(rowId, { slot: firstSlot, outcome: markets[firstSlot]?.sides[0]?.outcome ?? 0, main: markets[firstSlot]?.main ?? null })}
      onHide={() => onHide({ rowId, event, entityName })}
      propsLink={propsOffer(event)}
      onOpenGame={() => onOpenGame(rowId)}
      compact={compact}
      wide={wide}
      playColumn={playColumn}
      onHoverStart={hover.start}
      onHoverEnd={hover.end}
    />
  );
});

export const LiveSubjectRow = memo(function LiveSubjectRow({
  league, family, rowId, event, selected, format, gridCols, drillOutcome, rowSelected, compact = false, bet, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  rowId: RowId;
  event: EventHeader | undefined;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  gridCols: string;
  drillOutcome: number | null;
  rowSelected: boolean;
  compact?: boolean;
  bet: BetActions;
  onOpen: (rowId: RowId, target: CellTarget) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const row = useRow(league, family.id, rowId, event, family.markets);
  const spec = rowSpec(family, row);
  const minStake = useContext(MinStakeContext);
  const markets = row === undefined ? [] : rowCells(row, [spec], event, selected, format, minStake);
  const flash = useRowCellsWithFlash(markets, { family: family.id, selected, format, minStake });
  const ladderEvent = ladderEventOf(event);
  const hover = useHoverPrefetch(league, ladderEvent, family.id, row?.entity?.key ?? "");
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEvent, family: family.id, entity: row?.entity?.key ?? "" }, markets, row?.entity?.name ?? matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, target));
  return (
    <SubjectRowView
      subject={subjectOf(family, row, event, league)}
      live={Boolean(event?.live)}
      count={spec.outcomes.length}
      market={markets[0] ?? null}
      flash={flash[0] ?? []}
      gridCols={gridCols}
      selected={rowSelected && drillOutcome !== null ? { slot: 0, outcome: drillOutcome } : null}
      rowSelected={rowSelected}
      compact={compact}
      onOpen={(target) => onOpen(rowId, target)}
      bet={cellBet}
      onHide={() => onHide({ rowId, event, entityName: row?.entity?.name ?? null })}
      onHoverStart={hover.start}
      onHoverEnd={hover.end}
    />
  );
});
